import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { CuraIcon } from '@rededor/cura-react'

import * as Sentry from '@sentry/react'

import { useIsActiveBreakPoint } from '@/hooks/useIsActiveBreakPoint'
import { useFallback } from '@/hooks/useFallback'

import { SideBar } from '@/components/SideBar'
import { Show } from '@/components/Show'
import { CallCenter } from '@/components/CallCenter'
import { ErrorPageContent } from '@/components/ErrorPageContent/page'

import { PagesWrapper } from '@/shared/styles/pagesWrapper'

import { useStepbarStore } from '@/store/useStepbarStore'
import { sendEvent } from '@/services/mixPanelService'

import * as S from './fallback/styles'
import { storageKeys } from '@/shared/consts/storageKeys'

interface ErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function Error({ error }: ErrorProps) {
  const pathName = useLocation().pathname
  const { sendEventFallback } = useFallback()

  const isMobile = useIsActiveBreakPoint('medium')
  const setStepWithError = useStepbarStore((s) => s.setStepWithError)
  const isSecondError =
    sessionStorage.getItem(storageKeys.genericErrorShown) === 'true'

  useEffect(() => {
    setStepWithError(pathName)

    return () => {
      setStepWithError('')
    }
  }, [setStepWithError, error, pathName])

  useEffect(() => {
    const sentryId = Sentry.captureException(error, {
      level: 'error',
      tags: {
        errorBoundary: 'error',
        errorName: error?.name,
        errorSource: 'react-error-boundary'
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        errorStack: error?.stack,
        errorDigest: error?.digest,
        originPage: window.location.href
      }
    })

    sendEvent({
      eventName: 'SHOW_GENERIC_ERROR',
      data: {
        TYPE: 'generic_error',
        SENTRY_EVENT_ID: sentryId,
        GENERIC_ERROR_ORIGIN_PAGE: window.location.href,
        GENERIC_ERROR_STATUS_TEXT: error.digest ?? error.message,
        GENERIC_ERROR_STACK: error?.stack,
        GENERIC_ERROR_MESSAGE:
          'Infelizmente, algo inesperado ocorreu e não conseguimos realizar este seu agendamento. Por favor, tente novamente.'
      }
    })

    sendEventFallback({
      type: 'erro-generico',
      label: `Infelizmente, algo inesperado ocorreu e não conseguimos realizar este
          seu agendamento. Por favor, tente novamente.`,
      message: error.digest ?? error.message
    })

    // captureError(error)
  }, [sendEventFallback, error])

  return (
    <PagesWrapper.Root>
      <PagesWrapper.SideBar>
        <SideBar.Root>
          <SideBar.ContentWithPadding>
            <SideBar.Title type="fallback">Atenção</SideBar.Title>
            <Show when={!isMobile && isSecondError}>
              <CallCenter.Button />
              <CallCenter.Info />
            </Show>
          </SideBar.ContentWithPadding>
        </SideBar.Root>
      </PagesWrapper.SideBar>
      <PagesWrapper.Body>
        <PagesWrapper.ContentWithIcon>
          <CuraIcon
            color="neutral-dark"
            name="alertTriangle"
            size={48}
            style={{
              marginTop: isMobile ? '0' : '3px'
            }}
          />
          <S.Content>
            <ErrorPageContent />
            <Show when={isMobile && isSecondError}>
              <CallCenter.Button />
              <CallCenter.Info />
            </Show>
          </S.Content>
        </PagesWrapper.ContentWithIcon>
      </PagesWrapper.Body>
    </PagesWrapper.Root>
  )
}
